import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Tenant, TenantFormData, TenantAdminInvitation, TenantDeletionRequest, TenantStatus } from "@/types/tenant";
import { useToast } from "@/hooks/use-toast";
import { TenantDTO, TenantRequestDTO } from "@/types/api";
import * as tenantsApi from "@/services/tenantsApi";
import * as devicesApi from "@/services/devicesApi";
import { setTenantCache } from "@/utils/customerLookup";

import { parseDateTime } from '@/utils/dateUtils';

/**
 * Map production API TenantDTO → UI Tenant interface.
 * The API stores most fields at the top level (adminEmail, status, etc.)
 * while config may contain additional custom settings.
 */
function mapDtoToTenant(dto: TenantDTO): Tenant {
  const cfg = (dto.config || {}) as Record<string, any>;
  return {
    id: dto.id,
    name: dto.name,
    status: dto.status,
    tenant_status: (dto.status?.toLowerCase() || 'active') as TenantStatus,
    email: dto.adminEmail || cfg.email || '',
    phone: cfg.phone || null,
    address: cfg.address || null,
    city: cfg.city || null,
    state: cfg.state || null,
    zip: cfg.zip || null,
    country: cfg.country || null,
    logo_url: cfg.logo_url || null,
    portal_url: cfg.portal_url || null,
    subscription_tier: cfg.subscription_tier || null,
    industry: cfg.industry || null,
    company_size: cfg.company_size || null,
    billing_contact_name: cfg.billing_contact_name || null,
    billing_contact_email: cfg.billing_contact_email || null,
    device_count: cfg.device_count ?? 0,
    expected_device_count: cfg.expected_device_count || null,
    onboarded_by: dto.createdBy || cfg.onboarded_by || null,
    onboarded_at: parseDateTime(dto.createdAt) || cfg.onboarded_at || null,
    created_at: parseDateTime(dto.createdAt),
    updated_at: parseDateTime((dto as any).updatedAt) || null,
    updated_by: (dto as any).updatedBy || null,
    partner_id: (dto as any).partnerId || null,
    domains: Array.isArray((dto as any).domains) ? (dto as any).domains : null,
  };
}

/**
 * Map UI TenantFormData → API TenantRequestDTO.
 */
function mapFormToRequest(formData: TenantFormData): TenantRequestDTO {
  return {
    name: formData.name,
    status: 'ACTIVE',
    config: {
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      country: formData.country,
      logo_url: formData.logo_url,
      industry: formData.industry,
      company_size: formData.company_size,
      expected_device_count: formData.expected_device_count,
      billing_contact_name: formData.billing_contact_name,
      billing_contact_email: formData.billing_contact_email,
      invite_admin: formData.invite_admin,
      admin_email: formData.admin_email,
      admin_name: formData.admin_name,
    },
  };
}

export function useTenants() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: async (): Promise<Tenant[]> => {
      try {
        const dtos = await tenantsApi.listTenants();
        const tenants = (dtos || []).map(mapDtoToTenant);
        setTenantCache(tenants.map(t => ({ id: t.id, name: t.name })));
        return tenants;
      } catch {
        return [];
      }
    },
  });

  // Fetch device counts by aggregating all devices across tenants
  const deviceCountsQuery = useQuery({
    queryKey: ["tenant-device-counts"],
    queryFn: async (): Promise<Map<string, number>> => {
      try {
        // Fetch a large page to get all devices for counting
        const res = await devicesApi.listDevices(0, 1000);
        const resData = (res as any)?.data;
        let rawList: any[] = [];
        if (Array.isArray(resData)) rawList = resData;
        else if (resData?.content && Array.isArray(resData.content)) rawList = resData.content;
        else if (Array.isArray(res)) rawList = res;

        const counts = new Map<string, number>();
        rawList.forEach((d: any) => {
          const tid = d.tenantId;
          if (tid) counts.set(tid, (counts.get(tid) || 0) + 1);
        });
        return counts;
      } catch {
        return new Map();
      }
    },
    staleTime: 1000 * 60,
  });

  const tenantByIdQuery = (id: string) => useQuery({
    queryKey: ["tenant", id],
    queryFn: async (): Promise<Tenant | null> => {
      try {
        const dto = await tenantsApi.getTenant(id);
        return dto ? mapDtoToTenant(dto) : null;
      } catch {
        return null;
      }
    },
    enabled: !!id,
  });

  const createTenantMutation = useMutation({
    mutationFn: async (formData: TenantFormData) => {
      const request = mapFormToRequest(formData);
      return await tenantsApi.createTenant(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast({ title: "Tenant Created" });
    },
    onError: (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateTenantMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TenantFormData> }) => {
      const request: TenantRequestDTO = {
        name: data.name,
        config: { ...data },
      };
      return await tenantsApi.updateTenantConfig(id, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast({ title: "Tenant Updated" });
    },
    onError: (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateTenantStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: TenantStatus }) => {
      return await tenantsApi.updateTenantStatus(id, { status: status.toUpperCase() });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
  });

  const deviceCounts = deviceCountsQuery.data || new Map<string, number>();
  const tenants = (tenantsQuery.data || []).map(t => ({
    ...t,
    device_count: deviceCounts.get(t.id) || t.device_count || 0,
  }));
  const licenseUsage = {
    totalDevices: tenants.reduce((sum, t) => sum + (t.device_count || 0), 0),
    activeTenants: tenants.filter(t => t.tenant_status === 'active').length,
    pendingTenants: tenants.filter(t => t.tenant_status === 'creating' || t.tenant_status === 'incomplete').length,
  };

  return {
    tenants,
    isLoading: tenantsQuery.isLoading,
    error: tenantsQuery.error,
    refetch: tenantsQuery.refetch,
    getTenantById: tenantByIdQuery,
    createTenant: createTenantMutation.mutateAsync,
    isCreating: createTenantMutation.isPending,
    updateTenant: updateTenantMutation.mutateAsync,
    isUpdating: updateTenantMutation.isPending,
    updateTenantStatus: updateTenantStatusMutation.mutateAsync,
    licenseUsage,
    isLoadingLicenseUsage: tenantsQuery.isLoading,
  };
}

export function useTenantInvitations(_customerId?: string) {
  return { invitations: [] as TenantAdminInvitation[], isLoading: false, cancelInvitation: async (_id: string) => {} };
}

export function useTenantDeletions() {
  return { deletions: [] as TenantDeletionRequest[], isLoading: false, requestDeletion: async (_params: any) => {}, isRequesting: false };
}
